import { Injectable } from '@angular/core';
import { ApiClient } from '@canvas/four-r-digital';
import { Observable, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CreatePaymentResponse,
  FinanceAsset,
  FinanceOverviewResponse,
  PartnerContribution,
  PaymentDraft,
  PaymentRecord,
} from './finance.models';

@Injectable({ providedIn: 'root' })
export class FinanceApiService {
  private readonly baseUrl = `${environment.baseApiUrl}finance`;
  private readonly useLocal = environment.useLocal;
  private readonly localOverviewStorageKey = 'finance-pwa-overview';

  constructor(private readonly apiClient: ApiClient) {}

  getOverview(): Observable<FinanceOverviewResponse> {
    if (this.useLocal) {
      return of(this.getLocalOverview());
    }

    return this.apiClient.get<FinanceOverviewResponse>(`${this.baseUrl}/overview`);
  }

  getAssets(): Observable<FinanceAsset[]> {
    if (this.useLocal) {
      return of(this.getLocalOverview().assets);
    }

    return this.apiClient.get<FinanceAsset[]>(`${this.baseUrl}/assets`);
  }

  getPartners(): Observable<PartnerContribution[]> {
    if (this.useLocal) {
      return of(this.getLocalOverview().partners);
    }

    return this.apiClient.get<PartnerContribution[]>(`${this.baseUrl}/partners`);
  }

  getPayments(): Observable<PaymentRecord[]> {
    if (this.useLocal) {
      return of(this.getLocalOverview().payments);
    }

    return this.apiClient.get<PaymentRecord[]>(`${this.baseUrl}/payments`);
  }

  createPayment(draft: PaymentDraft): Observable<CreatePaymentResponse> {
    if (this.useLocal) {
      const payment: PaymentRecord = {
        id: `FIN-LOCAL-${Date.now()}`,
        targetName: draft.targetId,
        amount: draft.amount,
        method: draft.method,
        createdOn: new Date().toISOString(),
        status: 'Completed',
      };

      return of({
        reference: payment.id,
        payment,
      });
    }

    return this.apiClient.post<CreatePaymentResponse>(
      `${this.baseUrl}/payments`,
      draft
    );
  }

  isUsingLocalData(): boolean {
    return this.useLocal;
  }

  saveLocalOverview(overview: FinanceOverviewResponse): void {
    if (!this.useLocal) {
      return;
    }

    this.writeLocalOverview(overview);
  }

  private getLocalOverview(): FinanceOverviewResponse {
    const stored = this.readLocalOverview();
    if (stored) {
      return stored;
    }

    const seeded = this.buildOverview();
    this.writeLocalOverview(seeded);
    return seeded;
  }

  private readLocalOverview(): FinanceOverviewResponse | null {
    const storage = this.getSessionStorage();
    if (!storage) {
      return null;
    }

    const raw = storage.getItem(this.localOverviewStorageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<FinanceOverviewResponse>;
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        !parsed.summary ||
        !Array.isArray(parsed.assets) ||
        !Array.isArray(parsed.partners) ||
        !Array.isArray(parsed.payments)
      ) {
        return null;
      }

      const normalized = parsed as FinanceOverviewResponse;
      normalized.partners = normalized.partners.map((partner) =>
        partner.id === 'p-1' || partner.name === 'Amara Okafor'
          ? { ...partner, name: 'Nick Hughes' }
          : partner
      );
      normalized.assets = normalized.assets.map((asset) => {
        if (
          asset.id === 'asset-4' &&
          (asset.lastKnownPosition?.label === 'Embakasi, Nairobi' ||
            (asset.lastKnownPosition?.latitude === -1.3191 &&
              asset.lastKnownPosition?.longitude === 36.9278))
        ) {
          return {
            ...asset,
            lastKnownPosition: {
              latitude: -1.3197,
              longitude: 36.7043,
              label: 'Karen, Nairobi',
            },
          };
        }

        return asset;
      });

      return normalized;
    } catch {
      return null;
    }
  }

  private writeLocalOverview(overview: FinanceOverviewResponse): void {
    const storage = this.getSessionStorage();
    if (!storage) {
      return;
    }

    storage.setItem(this.localOverviewStorageKey, JSON.stringify(overview));
  }

  private getSessionStorage(): Storage | null {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }

    return window.sessionStorage;
  }

  private buildOverview(): FinanceOverviewResponse {
    return {
      summary: {
        totalEquity: 1482900,
        paid: 1150900,
        remaining: 332000,
        healthPercent: 78,
        growthSeries: [220, 240, 255, 300, 340, 335, 390, 430, 465, 510, 545, 590],
        projectedRepayment: [120, 170, 220, 260, 300, 340],
        actualRepayment: [130, 155, 245, 295, 360, 420],
      },
      assets: [
        {
          id: 'asset-1',
          name: 'Skoot TukTuk',
          category: 'Mobility',
          description:
            'High-efficiency cargo model for regional logistics in Nairobi.',
          assetUserName: 'Brian Mwangi',
          lastOnlineAt: new Date(Date.now() - 16 * 60 * 1000).toISOString(),
          imageUrl: '/assets/assets/tuktuk.jpeg',
          paid: 2275,
          total: 3500,
          velocitySeries: [3, 5, 4, 7, 8, 6, 9],
          paymentStatus: 'On Track',
          usageSeries: [61, 64, 66, 70, 73, 75, 78],
          lastKnownPosition: {
            latitude: -1.2863,
            longitude: 36.8174,
            label: 'Westlands, Nairobi',
          },
        },
        {
          id: 'asset-2',
          name: 'Roam Air Electric',
          category: 'Mobility',
          description: 'Last-mile e-bike with dual battery capacity.',
          assetUserName: 'Wanjiku Njeri',
          lastOnlineAt: new Date(Date.now() - 52 * 60 * 1000).toISOString(),
          imageUrl:
            'https://images.unsplash.com/photo-1511994298241-608e28f14fde?auto=format&fit=crop&w=900&q=80',
          paid: 720,
          total: 1800,
          velocitySeries: [2, 3, 5, 6, 8, 10, 10],
          paymentStatus: 'Accelerated',
          usageSeries: [45, 50, 58, 63, 69, 74, 79],
          lastKnownPosition: {
            latitude: -1.3008,
            longitude: 36.7821,
            label: 'Kilimani, Nairobi',
          },
        },
        {
          id: 'asset-3',
          name: 'SunCulture RainMaker',
          category: 'Agri-Tech',
          description: 'Smart irrigation pump with solar controller.',
          assetUserName: 'Samuel Otieno',
          lastOnlineAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          imageUrl:
            'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=900&q=80',
          paid: 1020,
          total: 1200,
          velocitySeries: [8, 9, 9, 9, 8, 9, 10],
          paymentStatus: 'Near Final',
          usageSeries: [72, 75, 77, 79, 81, 83, 86],
          lastKnownPosition: {
            latitude: -1.2556,
            longitude: 36.8849,
            label: 'Ruaka Edge, Nairobi Metro',
          },
        },
        {
          id: 'asset-4',
          name: 'D.light Solar Home',
          category: 'Energy',
          description:
            'Residential energy system with battery storage and smart controller.',
          assetUserName: 'Akinyi Naliaka',
          lastOnlineAt: new Date(Date.now() - 27 * 60 * 60 * 1000).toISOString(),
          imageUrl:
            'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=900&q=80',
          paid: 484,
          total: 2200,
          velocitySeries: [6, 4, 2, 0, 1, 2, 4],
          paymentStatus: 'Delayed',
          usageSeries: [39, 35, 28, 22, 24, 27, 31],
          lastKnownPosition: {
            latitude: -1.3197,
            longitude: 36.7043,
            label: 'Karen, Nairobi',
          },
        },
      ],
      partners: [
        { id: 'p-1', name: 'Nick Hughes', contribution: 342000, verified: true },
        { id: 'p-2', name: 'Joseph Mensah', contribution: 296000, verified: true },
        { id: 'p-3', name: 'Aisha Bello', contribution: 264000, verified: true },
        { id: 'p-4', name: 'Nia Dlamini', contribution: 248000, verified: false },
      ],
      payments: [
        {
          id: 'FIN-104421',
          targetName: 'Lagos Tech Hub IV',
          amount: 1500,
          method: 'bank',
          createdOn: new Date().toISOString(),
          status: 'Completed',
        },
        {
          id: 'FIN-104097',
          targetName: 'SunCulture RainMaker',
          amount: 950,
          method: 'card',
          createdOn: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          status: 'Completed',
        },
      ],
    };
  }
}
